import { differenceInYears } from 'date-fns';
import _ from 'lodash';
import {
    Address,
    PlaceOfServiceType,
    ProviderFacility,
    ProviderGuarantor,
    ProviderPatient,
    ProviderPayer,
    ProviderSubscriber,
    ProviderSubscriberPatient,
    SupportFacility,
    SupportGuarantor,
    SupportPatient,
    SupportPayer,
    SupportSubscriber,
    SupportSubscriberPatient,
} from '../../../api';
import { formatDate, normalizeStr } from '../../../services';
import { formatPhoneNumber } from '../cards/cards';

export const formatPersonName = (
    first?: string,
    last?: string,
    suffix?: string,
    organization?: string,
): string => {
    if (organization && !last) {
        return organization;
    }

    const base = _([first, last]).compact().join(' ');
    return base ? _([base, suffix]).compact().join(' ') : '';
};

export const formatAddress = (addresses?: Address[]): string | undefined => {
    const address = _.first(addresses);
    if (!address) {
        return;
    }

    const parts: string[] = [];

    if (address.lines?.length) {
        parts.push(...address.lines);
    }

    const cityStatePostal = _.compact([address.city, address.state, address.postal]).join(', ');
    if (cityStatePostal) {
        parts.push(cityStatePostal);
    }

    return parts.join(', ');
};

export const formatPatientDemographics = (patient?: ProviderPatient | SupportPatient): string | undefined => {
    if (!patient) {
        return;
    }

    const age = patient.dob ? differenceInYears(new Date(), new Date(patient.dob)) : undefined;

    return _.compact([
        patient.sex ? _.upperFirst(_.lowerCase(patient.sex)) : undefined,
        patient.dob ? `Born ${formatDate(patient.dob, 'MMMM d, yyyy')}` : undefined,
        age !== undefined ? `${age} yrs` : undefined,
    ]).join(' · ');
};

export const formatSubscriberSummary = (
    subscriber?: ProviderSubscriber | SupportSubscriber,
): string | undefined => {
    if (!subscriber) {
        return;
    }

    const name = formatPersonName(subscriber.first, subscriber.last, subscriber.suffix, subscriber.organization);
    const sex = subscriber.sex ? _.upperFirst(_.lowerCase(subscriber.sex)) : undefined;
    const dob = subscriber.dob ? `Born on ${formatDate(subscriber.dob, 'MMM d, yyyy')}` : undefined;

    return _.compact([name, sex, dob]).join(' · ') || undefined;
};

export const formatPayerTitle = (payer?: ProviderPayer | SupportPayer): string | undefined => {
    if (!payer) {
        return;
    }

    return payer.name;
};

/** Coverage and optional payer description for the card subtitle. */
export const formatPayerDescription = (payer?: ProviderPayer | SupportPayer): string | undefined => {
    if (!payer) {
        return;
    }

    let description = payer.description || '';
    if (payer.coverage) {
        description += `${description ? ' ' : ''}(${normalizeStr(payer.coverage)})`;
    }
    return description || undefined;
};

export const formatGuarantorName = (guarantor?: ProviderGuarantor | SupportGuarantor): string | undefined => {
    if (!guarantor) {
        return;
    }

    return formatPersonName(guarantor.first, guarantor.last, guarantor.suffix, guarantor.organization);
};

export const formatRelationshipLabel = (
    subscriberPatient?: ProviderSubscriberPatient | SupportSubscriberPatient,
): string | undefined => {
    if (!subscriberPatient?.relationship) {
        return;
    }

    return `Relationship · ${_.upperFirst(_.lowerCase(subscriberPatient.relationship))}`;
};

export const formatPhone = (phone?: string): string | undefined => {
    if (!phone) {
        return;
    }

    return formatPhoneNumber(phone) || phone;
};

/** CMS Place of Service numeric codes. UNKNOWN has no official code. */
const PLACE_OF_SERVICE_CMS_CODES: Record<PlaceOfServiceType, string | undefined> = {
    [PlaceOfServiceType.PHARMACY]: '01',
    [PlaceOfServiceType.TELEHEALTH]: '02',
    [PlaceOfServiceType.SCHOOL]: '03',
    [PlaceOfServiceType.HOMELESSSHELTER]: '04',
    [PlaceOfServiceType.INDIANHEALTHSERVICEFREESTANDINGFACILITY]: '05',
    [PlaceOfServiceType.INDIANHEALTHSERVICEPROVIDERBASEDFACILITY]: '06',
    [PlaceOfServiceType.TRIBAL638FREESTANDINGFACILITY]: '07',
    [PlaceOfServiceType.TRIBAL638PROVIDERBASEDFACILITY]: '08',
    [PlaceOfServiceType.CORRECTIONALFACILITY]: '09',
    [PlaceOfServiceType.TELEHEALTHPROVIDEDINPATIENTSHOME]: '10',
    [PlaceOfServiceType.OFFICE]: '11',
    [PlaceOfServiceType.HOME]: '12',
    [PlaceOfServiceType.ASSISTEDLIVINGFACILITY]: '13',
    [PlaceOfServiceType.GROUPHOME]: '14',
    [PlaceOfServiceType.MOBILEUNIT]: '15',
    [PlaceOfServiceType.TEMPORARYLODGING]: '16',
    [PlaceOfServiceType.WALKINRETAILHEALTHCLINIC]: '17',
    [PlaceOfServiceType.PLACEOFEMPLOYMENTWORKSITE]: '18',
    [PlaceOfServiceType.OFFCAMPUSOUTPATIENTHOSPITAL]: '19',
    [PlaceOfServiceType.URGENTCAREFACILITY]: '20',
    [PlaceOfServiceType.INPATIENTHOSPITAL]: '21',
    [PlaceOfServiceType.OUTPATIENTHOSPITAL]: '22',
    [PlaceOfServiceType.ONCAMPUSOUTPATIENTHOSPITAL]: '22',
    [PlaceOfServiceType.EMERGENCYROOMHOSPITAL]: '23',
    [PlaceOfServiceType.AMBULATORYSURGICALCENTER]: '24',
    [PlaceOfServiceType.BIRTHINGCENTER]: '25',
    [PlaceOfServiceType.MILITARYTREATMENTFACILITY]: '26',
    [PlaceOfServiceType.SKILLEDNURSINGFACILITY]: '31',
    [PlaceOfServiceType.NURSINGFACILITY]: '32',
    [PlaceOfServiceType.CUSTODIALCAREFACILITY]: '33',
    [PlaceOfServiceType.HOSPICE]: '34',
    [PlaceOfServiceType.AMBULANCELAND]: '41',
    [PlaceOfServiceType.AMBULANCEAIRORWATER]: '42',
    [PlaceOfServiceType.INDEPENDENTCLINIC]: '49',
    [PlaceOfServiceType.FEDERALLYQUALIFIEDHEALTHCENTER]: '50',
    [PlaceOfServiceType.INPATIENTPSYCHIATRICFACILITY]: '51',
    [PlaceOfServiceType.PSYCHIATRICFACILITYPARTIALHOSPITALIZATION]: '52',
    [PlaceOfServiceType.COMMUNITYMENTALHEALTHCENTER]: '53',
    [PlaceOfServiceType.INTERMEDIATECAREFACILITY]: '54',
    [PlaceOfServiceType.RESIDENTIALSUBSTANCEABUSETREATMENTFACILITY]: '55',
    [PlaceOfServiceType.PSYCHIATRICRESIDENTIALTREATMENTCENTER]: '56',
    [PlaceOfServiceType.NONRESIDENTIALSUBSTANCEABUSETREATMENTFACILITY]: '57',
    [PlaceOfServiceType.MASSIMMUNIZATIONCENTER]: '60',
    [PlaceOfServiceType.COMPREHENSIVEINPATIENTREHABILITATIONFACILITY]: '61',
    [PlaceOfServiceType.COMPREHENSIVEOUTPATIENTREHABILITATIONFACILITY]: '62',
    [PlaceOfServiceType.ENDSTAGERENALDISEASETREATMENTFACILITY]: '65',
    [PlaceOfServiceType.PUBLICHEALTHCLINIC]: '71',
    [PlaceOfServiceType.RURALHEALTHCLINIC]: '72',
    [PlaceOfServiceType.INDEPENDENTLABORATORY]: '81',
    [PlaceOfServiceType.OTHERPLACEOFSERVICE]: '99',
    [PlaceOfServiceType.UNKNOWN]: undefined,
};

export const formatPlaceOfService = (placeOfService?: PlaceOfServiceType): string | undefined => {
    if (!placeOfService || placeOfService === PlaceOfServiceType.UNKNOWN) {
        return;
    }

    const label = _.startCase(_.toLower(placeOfService));
    const code = PLACE_OF_SERVICE_CMS_CODES[placeOfService];

    return code ? `${code} · ${label}` : label;
};

export const formatFacilityPhone = (facility?: ProviderFacility | SupportFacility): string | undefined => {
    const registry = facility && 'registry' in facility ? facility.registry : undefined;
    const teleNumber = _.find(registry?.addresses, address => Boolean(address.teleNumber))?.teleNumber;

    return formatPhone(teleNumber);
};
